import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import SearchResultList from 'components/tools/SearchPage/SearchResultList';
import * as searchTypes from 'constants/searchTypes';
import {
  ExtraRow,
  ResultsCancelSubscriptionBox,
  ResultsTable,
  SearchSectionLabel,
  TableBody,
  LabelRow,
} from './SearchResults.styles';
import { clearSelectedItems, selectOrder } from '../../../../../../actions/tools/OrdersSearchActions';
import {
  selectSubscriptionInDecisionTree,
  unSelectAllSubscriptionsInDecisionTree,
  unSelectSubscriptionInDecisionTree,
} from '../../../../../../actions/tools/CommonSubscriptionActions';
import {
  getIsCommonSubscriptionSystemInstalled,
  getOrderingSystem,
  getOrdersSearchProperties,
  getSelectedOrder,
  getSelectedSubscriptionsIds,
  selectSubscriptions,
} from 'selectors/tools';
import ResultText from './ResultText/ResultText';
import ResultList from './ResultList/ResultList';

const getOrdersWithNoParentSubscriptionsPresent = (searchSubscription, ordersSearch) => {
  // gather list of all shopify variant ids in currently displayed subscriptions
  let shopifyVariantIdArray = [];
  if (!isEmpty(searchSubscription)) {
    shopifyVariantIdArray = searchSubscription.map((subscription) => subscription.shopify_variant_id);
  }

  // filter out orders that belong to a present subscription
  return ordersSearch.orders.filter((order) => {
    return (
      Array.isArray(order.line_items) &&
      !order.line_items.some((item) => shopifyVariantIdArray.includes(item.variant_id))
    );
  });
};

const SearchResults = ({ isSearchBothOrdersAndSubscriptions }) => {
  const dispatch = useDispatch();

  const searchSubscription = useSelector(selectSubscriptions);
  const orderingSystem = useSelector(getOrderingSystem);
  const ordersSearch = useSelector(getOrdersSearchProperties);
  const selectedSubscriptionsIds = useSelector(getSelectedSubscriptionsIds);
  const selectedOrder = useSelector(getSelectedOrder);
  const isCommonSubscriptionSystemInstalled = useSelector(getIsCommonSubscriptionSystemInstalled);

  const ordersWithNoParentSubscriptionsPresent = getOrdersWithNoParentSubscriptionsPresent(
    searchSubscription,
    ordersSearch
  );
  const shouldDisplayResultsOfSearch =
    searchSubscription.length || (isSearchBothOrdersAndSubscriptions && ordersSearch.orders.length);

  const subscriptionItemClickHandler = (subscription) => {
    if (isSearchBothOrdersAndSubscriptions) {
      // only allow one selected subscription or order
      // order logic must be added here
      dispatch(selectOrder({}));
      dispatch(unSelectAllSubscriptionsInDecisionTree());
    }
    if (selectedSubscriptionsIds.includes(subscription.id)) {
      dispatch(unSelectSubscriptionInDecisionTree(subscription.id));
    } else {
      dispatch(selectSubscriptionInDecisionTree(subscription.id));
    }
  };

  // for orders, not subscriptions
  const shopifyOrderClickHandler = (order) => {
    // clear all selected subscriptions
    dispatch(unSelectAllSubscriptionsInDecisionTree());
    if (selectedOrder.id !== order.id) {
      // clear previously selected items
      dispatch(clearSelectedItems());
      dispatch(selectOrder(order));
    }
  };

  if (shouldDisplayResultsOfSearch) {
    return (
      <ResultsCancelSubscriptionBox key={'orderResults'}>
        <ResultsTable className="table table-hover results-table">
          <ResultText
            isSearchBothOrdersAndSubscriptions={isSearchBothOrdersAndSubscriptions}
            ordersWithNoParentSubscriptionsPresent={ordersWithNoParentSubscriptionsPresent}
          />
          <TableBody
            currentHeight={window.innerHeight}
            searchBarHeight={document.getElementById('searchHeader')?.offsetHeight || '90px'}
          >
            {(() => {
              if (isSearchBothOrdersAndSubscriptions) {
                return (
                  <>
                    {!isEmpty(searchSubscription) && (
                      <>
                        <LabelRow>
                          <SearchSectionLabel>Subscriptions</SearchSectionLabel>
                        </LabelRow>
                        <SearchResultList
                          listType={searchTypes.SEARCH_CANCEL_SUBSCRIPTIONS_DECISION_TREE}
                          resultList={searchSubscription}
                          selectedSubscriptionsIds={selectedSubscriptionsIds}
                          resultClickAction={subscriptionItemClickHandler}
                          cancelSubscription={true}
                          zendeskConfig={{ isZendesk: false }}
                        />
                      </>
                    )}
                    {!isEmpty(ordersWithNoParentSubscriptionsPresent) && (
                      <>
                        <LabelRow>
                          <SearchSectionLabel>Orders</SearchSectionLabel>
                        </LabelRow>
                        <SearchResultList
                          listType={searchTypes.SEARCH_ORDERS_DECISION_TREE}
                          resultList={ordersWithNoParentSubscriptionsPresent}
                          resultClickAction={shopifyOrderClickHandler}
                          orderingSystem={orderingSystem}
                        />
                      </>
                    )}
                  </>
                );
              } else if (isCommonSubscriptionSystemInstalled) {
                return (
                  <ResultList
                    resultList={searchSubscription}
                    selectedSubscriptionsIds={selectedSubscriptionsIds}
                    resultClickAction={subscriptionItemClickHandler}
                  />
                );
              }

              return (
                <SearchResultList
                  listType={searchTypes.SEARCH_CANCEL_SUBSCRIPTIONS_DECISION_TREE}
                  resultList={searchSubscription}
                  selectedSubscriptionsIds={selectedSubscriptionsIds}
                  resultClickAction={subscriptionItemClickHandler}
                  cancelSubscription={true}
                  zendeskConfig={{ isZendesk: false }}
                />
              );
            })()}
            <ExtraRow />
          </TableBody>
        </ResultsTable>
      </ResultsCancelSubscriptionBox>
    );
  }
};

export default SearchResults;
